import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip, Warning } from '../../components/LessonUI';

const HTMLHistory = () => {
    const firstHtmlDocCode = `<TITLE>The World Wide Web project</TITLE>
<H1>World Wide Web</H1>
The WorldWideWeb (W3) is a wide-area
<A HREF="WhatIs.html">hypermedia</A> information retrieval
initiative aiming to give universal access to a large universe
of documents.`;

    const html4StrictCode = `<!DOCTYPE HTML PUBLIC "-//W3C//DTD HTML 4.01//EN"
    "http://www.w3.org/TR/html4/strict.dtd">
<html>
<head>
    <title>HTML 4.01 Document</title>
    <link rel="stylesheet" href="styles.css">
</head>
<body>
    <h1>Modern Structure</h1>
    <p>No more &lt;font&gt; tags!</p>
</body>
</html>`;

    const modernHtml5Code = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>HTML5 Page</title>
</head>
<body>
    <header>
        <nav>
            <ul>
                <li><a href="#home">Home</a></li>
                <li><a href="#about">About</a></li>
            </ul>
        </nav>
    </header>

    <main>
        <article>
            <h1>Welcome to HTML5</h1>
            <p>Clean, semantic, and powerful.</p>
            <video controls>
                <source src="video.mp4" type="video/mp4">
                Your browser doesn't support video.
            </video>
        </article>
    </main>

    <footer>
        <p>&copy; 2025 My Website</p>
    </footer>
</body>
</html>`;

    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Web Evolution Masterclass</div>
                <h1>The Evolution of HTML: From HTML1 to HTML5</h1>
                <p className="hero-lead">
                    Understanding HTML's history reveals why modern web development works the way it does.
                    From Tim Berners-Lee's first proposal to today's living standard, HTML has continuously evolved.
                </p>
            </div>

            <div className="content-section">
                <h2>The Birth of the Web (1989-1991)</h2>
                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        In 1989, Tim Berners-Lee, a scientist at CERN, proposed a revolutionary information management system
                        that would become the World Wide Web. HTML was born from this vision.
                    </div></ProTip>

                <div className="timeline-container">
                    <div className="timeline-item">
                        <div className="timeline-year">1989</div>
                        <div className="timeline-content">
                            <strong>Tim Berners-Lee's Proposal:</strong> "Information Management: A Proposal"
                            laid the groundwork for the Web, including the concept of hypertext documents.
                        </div>
                    </div>
                    <div className="timeline-item">
                        <div className="timeline-year">1991</div>
                        <div className="timeline-content">
                            <strong>HTML 1.0 (Unofficial):</strong> The first public description of HTML.
                            It had only 18 tags, inspired by SGML (Standard Generalized Markup Language).
                        </div>
                    </div>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>First HTML Document (1991)</span>
                    </div>
                    <CodeBlock language="html" code={firstHtmlDocCode} />
                </div>

                <div className="explanation-list">
                    <h3>Original HTML Tags (1991):</h3>
                    <ul>
                        <li><code>&lt;TITLE&gt;</code> - Document title</li>
                        <li><code>&lt;H1&gt;</code> through <code>&lt;H6&gt;</code> - Headings</li>
                        <li><code>&lt;P&gt;</code> - Paragraphs</li>
                        <li><code>&lt;A&gt;</code> - Anchors/Links</li>
                        <li><code>&lt;ISINDEX&gt;</code> - Search queries (obsolete now)</li>
                        <li><code>&lt;PLAINTEXT&gt;</code> - Plain text (obsolete now)</li>
                        <li><code>&lt;LISTING&gt;</code> - Code listing (obsolete now)</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>HTML 2.0: The First Official Standard (1995)</h2>
                <p>
                    HTML 2.0 was the first standardized version, published as RFC 1866 by the Internet Engineering Task Force (IETF).
                    It formalized existing practices and added crucial features.
                </p>

                <div className="feature-grid">
                    <div className="feature-card">
                        <div className="card-icon"></div>
                        <h3>Forms Introduction</h3>
                        <p>The <code>&lt;FORM&gt;</code> tag enabled user input, revolutionizing web interactivity.</p>
                    </div>
                    <div className="feature-card">
                        <div className="card-icon"></div>
                        <h3>Images</h3>
                        <p>The <code>&lt;IMG&gt;</code> tag brought visual content to the Web.</p>
                    </div>
                    <div className="feature-card">
                        <div className="card-icon"></div>
                        <h3>Tables</h3>
                        <p>Basic table support for data presentation (though not layoutthat came later).</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>HTML 3.2: Netscape vs Internet Explorer (1997)</h2>
                <p>
                    The mid-90s saw the "Browser Wars" between Netscape Navigator and Internet Explorer.
                    Both browsers added proprietary tags, fragmenting the Web. HTML 3.2 attempted to standardize the chaos.
                </p>

                <div className="comparison-table">
                    <h3>Major Additions in HTML 3.2:</h3>
                    <table>
                        <thead>
                            <tr>
                                <th>Feature</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>&lt;SCRIPT&gt;</code></td>
                                <td>Embedded client-side scripts (JavaScript)</td>
                            </tr>
                            <tr>
                                <td><code>&lt;STYLE&gt;</code></td>
                                <td>Embedded CSS stylesheets</td>
                            </tr>
                            <tr>
                                <td><code>&lt;FONT&gt;</code></td>
                                <td>Text styling (now obsolete)</td>
                            </tr>
                            <tr>
                                <td><code>&lt;APPLET&gt;</code></td>
                                <td>Java applets (now obsolete)</td>
                            </tr>
                            <tr>
                                <td>Table enhancements</td>
                                <td>Tables became complex enough for layout (unfortunately)</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <Warning><strong> The Dark Ages:</strong> HTML 3.2 codified many presentational elements
                    (like <code>&lt;FONT&gt;</code>, <code>&lt;CENTER&gt;</code>) that mixed structure with style.
                    This led to unmaintainable code and would take years to undo.</Warning>
            </div>

            <div className="content-section">
                <h2>HTML 4.01: Separation of Concerns (1999)</h2>
                <p>
                    HTML 4.01 marked a philosophical shift: <strong>separate structure (HTML) from presentation (CSS)</strong>.
                    The W3C urged developers to abandon presentational markup.
                </p>

                <div className="concept-grid">
                    <div className="concept-card">
                        <h3>Three Flavors</h3>
                        <ul>
                            <li><strong>Strict:</strong> Deprecated tags forbidden</li>
                            <li><strong>Transitional:</strong> Deprecated tags allowed (for migration)</li>
                            <li><strong>Frameset:</strong> For frame-based layouts (thankfully dead now)</li>
                        </ul>
                    </div>
                    <div className="concept-card">
                        <h3>Key Improvements</h3>
                        <ul>
                            <li>Better accessibility (alt text requirements)</li>
                            <li>Internationalization (Unicode support)</li>
                            <li>Deprecated presentational tags</li>
                            <li>Enhanced forms and tables</li>
                        </ul>
                    </div>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>HTML 4.01 Strict DOCTYPE</span>
                    </div>
                    <CodeBlock language="html" code={html4StrictCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>XHTML: XML Meets HTML (2000-2009)</h2>
                <p>
                    XHTML reformulated HTML as an XML application, enforcing strict syntax rules.
                    The goal: cleaner, more predictable code.
                </p>

                <div className="comparison-table">
                    <h3>XHTML Requirements:</h3>
                    <table>
                        <thead>
                            <tr>
                                <th>Rule</th>
                                <th>HTML</th>
                                <th>XHTML</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><strong>Closing tags</strong></td>
                                <td><code>&lt;p&gt;Text</code> (optional close)</td>
                                <td><code>&lt;p&gt;Text&lt;/p&gt;</code> (required)</td>
                            </tr>
                            <tr>
                                <td><strong>Self-closing</strong></td>
                                <td><code>&lt;br&gt;</code></td>
                                <td><code>&lt;br /&gt;</code></td>
                            </tr>
                            <tr>
                                <td><strong>Case sensitivity</strong></td>
                                <td><code>&lt;DIV&gt;</code> or <code>&lt;div&gt;</code></td>
                                <td><code>&lt;div&gt;</code> only (lowercase)</td>
                            </tr>
                            <tr>
                                <td><strong>Attribute quotes</strong></td>
                                <td><code>width=100</code> (optional)</td>
                                <td><code>width="100"</code> (required)</td>
                            </tr>
                            <tr>
                                <td><strong>Nesting</strong></td>
                                <td>Forgiving overlaps</td>
                                <td>Strict nesting required</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>XHTML's Legacy:</strong> While XHTML 2.0 was abandoned, its discipline influenced modern HTML.
                        Many developers still follow XHTML-style conventions (lowercase tags, quoted attributes) for code quality.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>HTML5: The Modern Era (2014-Present)</h2>
                <p>
                    After years of stagnation, a group of browser vendors formed WHATWG in 2004 to develop HTML5.
                    It became an official W3C standard in 2014, bringing revolutionary changes.
                </p>

                <div className="feature-grid-large">
                    <div className="feature-card">
                        <div className="card-icon"></div>
                        <h3>Native Multimedia</h3>
                        <p><code>&lt;video&gt;</code> and <code>&lt;audio&gt;</code> eliminated the need for Flash, Silverlight, and other plugins.</p>
                        <div className="code-snippet">
                            <CodeBlock language="html" code={`<video src="movie.mp4" controls></video>`} />
                        </div>
                    </div>

                    <div className="feature-card">
                        <div className="card-icon"></div>
                        <h3>Canvas & SVG</h3>
                        <p>Powerful graphics capabilities built into browsers.</p>
                        <div className="code-snippet">
                            <CodeBlock language="html" code={`<canvas id="myCanvas"></canvas>`} />
                        </div>
                    </div>

                    <div className="feature-card">
                        <div className="card-icon"></div>
                        <h3>Semantic Elements</h3>
                        <p><code>&lt;header&gt;</code>, <code>&lt;nav&gt;</code>, <code>&lt;article&gt;</code>, <code>&lt;section&gt;</code>, <code>&lt;footer&gt;</code> replace generic <code>&lt;div&gt;</code>s.</p>
                    </div>

                    <div className="feature-card">
                        <div className="card-icon"></div>
                        <h3>Enhanced Forms</h3>
                        <p>New input types: email, date, range, color, search, and built-in validation.</p>
                        <div className="code-snippet">
                            <CodeBlock language="html" code={`<input type="email" required>`} />
                        </div>
                    </div>

                    <div className="feature-card">
                        <div className="card-icon"></div>
                        <h3>Web Storage</h3>
                        <p>localStorage and sessionStorage for client-side data persistence.</p>
                    </div>

                    <div className="feature-card">
                        <div className="card-icon"></div>
                        <h3>Geolocation API</h3>
                        <p>Access user location with permission.</p>
                    </div>

                    <div className="feature-card">
                        <div className="card-icon"></div>
                        <h3>Offline Applications</h3>
                        <p>Application Cache (deprecated) and Service Workers for offline functionality.</p>
                    </div>

                    <div className="feature-card">
                        <div className="card-icon"></div>
                        <h3>Drag and Drop</h3>
                        <p>Native drag-and-drop API.</p>
                    </div>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Modern HTML5 Document</span>
                    </div>
                    <CodeBlock language="html" code={modernHtml5Code} />
                </div>
            </div>

            <div className="content-section">
                <h2>HTML Living Standard: Continuous Evolution</h2>
                <p>
                    In 2019, W3C and WHATWG agreed that HTML would be a "Living Standard"continuously updated
                    rather than versioned. There won't be an HTML6; instead, HTML evolves incrementally.
                </p>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>What This Means:</strong> New features are added as browsers implement them.
                        The standard reflects what browsers actually support, not aspirational goals years away.
                    </div></ProTip>

                <div className="recent-additions">
                    <h3>Recent/Upcoming Additions:</h3>
                    <ul>
                        <li><code>&lt;dialog&gt;</code> - Native modal dialogs</li>
                        <li><code>loading="lazy"</code> - Native lazy loading for images</li>
                        <li><code>&lt;details&gt;</code> and <code>&lt;summary&gt;</code> - Disclosure widgets</li>
                        <li>Popover API - Native popovers without JavaScript</li>
                        <li>Declarative Shadow DOM - Web Components without JavaScript</li>
                        <li><code>&lt;search&gt;</code> - Semantic search landmark</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Key Lessons from HTML's Evolution</h2>
                <div className="lesson-grid">
                    <div className="lesson-card">
                        <h3>1. Simplicity Wins</h3>
                        <p>HTML5's <code>&lt;!DOCTYPE html&gt;</code> vs. HTML 4's verbose DOCTYPE shows the trend toward simplicity.</p>
                    </div>
                    <div className="lesson-card">
                        <h3>2. Separation of Concerns</h3>
                        <p>Structure (HTML), Presentation (CSS), Behavior (JavaScript) must remain separate.</p>
                    </div>
                    <div className="lesson-card">
                        <h3>3. Semantics Matter</h3>
                        <p>Meaningful tags improve accessibility, SEO, and maintainability.</p>
                    </div>
                    <div className="lesson-card">
                        <h3>4. Backward Compatibility</h3>
                        <p>HTML5 maintained compatibility with existing content, ensuring smooth migration.</p>
                    </div>
                    <div className="lesson-card">
                        <h3>5. Native Over Plugins</h3>
                        <p>Built-in features (video, canvas) replaced proprietary plugins, improving security and performance.</p>
                    </div>
                    <div className="lesson-card">
                        <h3>6. Progressive Enhancement</h3>
                        <p>New features degrade gracefully, ensuring usability across all browsers.</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Why This History Matters</h2>
                <p>Understanding HTML's evolution helps you:</p>
                <ul>
                    <li><strong>Avoid deprecated patterns:</strong> Know why <code>&lt;font&gt;</code>, <code>&lt;center&gt;</code>, and table layouts are bad practice</li>
                    <li><strong>Embrace modern features:</strong> Use semantic HTML5 elements confidently</li>
                    <li><strong>Understand browser quirks:</strong> Legacy code patterns persist; knowing history explains why</li>
                    <li><strong>Future-proof your skills:</strong> Recognize trends (semantic, accessible, performant) and adapt</li>
                    <li><strong>Appreciate the "why":</strong> Design decisions make sense in historical context</li>
                </ul>
            </div>

        </div>
    );
};

export default HTMLHistory;

